import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Copy, UserPlus, Users, Mail, Send, Check, Clock, XCircle, RefreshCw } from "lucide-react";

function StatusPill({ status }) {
  const cfg = {
    sent: { label: "Delivered to SES", cls: "bg-emerald-100 text-emerald-800 border-emerald-200", Icon: Check },
    queued: { label: "Sending…", cls: "bg-amber-100 text-amber-800 border-amber-200", Icon: Clock },
    failed: { label: "Failed", cls: "bg-red-100 text-red-800 border-red-200", Icon: XCircle },
  }[status] || { label: status || "—", cls: "bg-muted text-muted-foreground border-border", Icon: Clock };
  const Ic = cfg.Icon;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${cfg.cls}`} data-testid={`invite-status-${status}`}>
      <Ic className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function MyInvitesPanel({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/referral/my-invites");
      setItems(data?.items || []);
    } catch {
      // silent — panel just shows empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="mt-6 pt-6 border-t border-border/60" data-testid="my-invites-panel">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Mail className="h-4 w-4" /> My invite deliveries ({items.length})
        </h3>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 text-xs" data-testid="my-invites-refresh">
          <RefreshCw className={`h-3 w-3 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">Status shown is the AWS SES send-time state. &ldquo;Delivered to SES&rdquo; means SES accepted the request — the recipient inbox may still bounce silently.</p>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground mt-4" data-testid="my-invites-empty">You haven&apos;t sent any email invites yet.</p>
      ) : (
        <div className="mt-3 divide-y divide-border/60 border border-border/60 rounded-xl overflow-hidden">
          {items.map((it) => (
            <div key={it.invite_id} className="p-3 flex flex-wrap items-center justify-between gap-2 bg-background" data-testid={`invite-row-${it.invite_id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate" data-testid={`invite-to-${it.invite_id}`}>{it.to_email}</div>
                <div className="text-[11px] text-muted-foreground">
                  Sent {new Date(it.created_at).toLocaleString()}
                  {it.sent_at && it.sent_at !== it.created_at && <> · Delivered {new Date(it.sent_at).toLocaleString()}</>}
                </div>
                {it.error && <div className="text-[11px] text-red-700 mt-0.5" data-testid={`invite-error-${it.invite_id}`}>Error: {it.error}</div>}
                {it.ses_message_id && <div className="text-[10px] text-muted-foreground font-mono truncate" title={it.ses_message_id}>SES ID: {it.ses_message_id}</div>}
              </div>
              <StatusPill status={it.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ConsumerProfile() {
  const { user, refresh } = useAuth();
  const [cities, setCities] = useState([]);
  const [name, setName] = useState(user?.name || "");
  const [city, setCity] = useState(user?.city || "");
  const [tree, setTree] = useState(null);

  useEffect(() => {
    api.get("/cities").then((r) => setCities(r.data));
    api.get("/referral/my-tree").then((r) => setTree(r.data));
  }, []);

  const save = async () => {
    try {
      await api.put("/profile", { name, city });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error("Could not update");
    }
  };

  const copyInvite = () => {
    const url = `${window.location.origin}/register?ref=${user.referral_code}`;
    navigator.clipboard.writeText(url);
    toast.success("Invite link copied");
  };

  const [inv, setInv] = useState({ email: "", message: "", busy: false });
  const [invitesTick, setInvitesTick] = useState(0);
  const sendInvite = async (e) => {
    e.preventDefault();
    setInv((s) => ({ ...s, busy: true }));
    try {
      const { data } = await api.post("/referral/invite-by-email", {
        email: inv.email,
        message: inv.message || undefined,
      });
      toast.success(`Invite queued to ${data.to}`);
      setInv({ email: "", message: "", busy: false });
      // Give the SES background task ~2s to move status queued → sent, then refresh.
      setTimeout(() => setInvitesTick((n) => n + 1), 500);
      setTimeout(() => setInvitesTick((n) => n + 1), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send invite");
      setInv((s) => ({ ...s, busy: false }));
    }
  };

  if (!user) return null;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-12 space-y-8">
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          <AvatarImage src={user.picture} />
          <AvatarFallback>{user.name?.[0]}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="font-heading text-3xl tracking-tighter">{user.name}</h1>
          <div className="text-sm text-muted-foreground">
            {user.email} · <Badge variant="secondary">{user.role}</Badge>
            {!user.is_approved && <Badge variant="outline" className="ml-1">Pending</Badge>}
          </div>
        </div>
      </div>

      <Card className="p-6 border-border/60 space-y-4">
        <h2 className="font-heading text-xl">Edit profile</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Name</label>
            <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Home city</label>
            <Select value={city || ""} onValueChange={setCity}>
              <SelectTrigger className="mt-1" data-testid="profile-city-select"><SelectValue placeholder="Choose your city" /></SelectTrigger>
              <SelectContent className="max-h-72">
                {cities.map((c) => (<SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <Button onClick={save} className="rounded-full" data-testid="profile-save-btn">Save changes</Button>
      </Card>

      <Card className="p-6 border-border/60">
        <h2 className="font-heading text-xl flex items-center gap-2">
          <UserPlus className="h-5 w-5" /> Your referral
        </h2>
        <p className="text-sm text-muted-foreground mt-1">Share with a community member to invite them.</p>
        <div className="mt-4 flex gap-2 items-center">
          <Input readOnly value={user.referral_code} className="font-mono text-lg tracking-widest" data-testid="profile-referral-code" />
          <Button variant="outline" onClick={copyInvite} className="rounded-full" data-testid="profile-copy-invite">
            <Copy className="h-4 w-4 mr-1" /> Copy link
          </Button>
        </div>

        <div className="mt-6 pt-6 border-t border-border/60">
          <h3 className="font-heading text-base flex items-center gap-2">
            <Mail className="h-4 w-4" /> Invite by email
          </h3>
          <p className="text-xs text-muted-foreground mt-1">
            We'll send a personalised invitation from <strong>info@sanskaribazar.in</strong> with your referral link baked in.
          </p>
          <form onSubmit={sendInvite} className="mt-3 grid sm:grid-cols-[1fr_auto] gap-2 items-start">
            <div className="space-y-2">
              <Input
                type="email"
                required
                placeholder="friend@example.com"
                value={inv.email}
                onChange={(e) => setInv((s) => ({ ...s, email: e.target.value }))}
                data-testid="invite-email-input"
              />
              <textarea
                rows={2}
                placeholder="Personal message (optional)"
                value={inv.message}
                onChange={(e) => setInv((s) => ({ ...s, message: e.target.value }))}
                data-testid="invite-message-input"
                className="w-full text-sm rounded-md border border-input bg-background px-3 py-2 ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <Button
              type="submit"
              disabled={inv.busy || !inv.email}
              className="rounded-full"
              data-testid="invite-send-btn"
            >
              <Send className="h-4 w-4 mr-1" /> {inv.busy ? "Sending…" : "Send invite"}
            </Button>
          </form>
        </div>

        <MyInvitesPanel refreshKey={invitesTick} />

        {tree && (
          <div className="mt-6">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Users className="h-4 w-4" /> Direct referrals ({tree.direct.length}) · 2nd-level ({tree.second_level.length})
            </h3>
            <div className="mt-3 grid sm:grid-cols-2 gap-2">
              {tree.direct.map((u) => (
                <div key={u.user_id} className="flex items-center justify-between p-3 rounded-xl border border-border/60 text-sm">
                  <div>
                    <div className="font-medium">{u.name}</div>
                    <div className="text-xs text-muted-foreground">{u.email}</div>
                  </div>
                  <Badge variant={u.is_approved ? "default" : "secondary"}>{u.is_approved ? "approved" : "pending"}</Badge>
                </div>
              ))}
              {tree.direct.length === 0 && (
                <p className="text-sm text-muted-foreground col-span-full">You haven't invited anyone yet.</p>
              )}
            </div>
          </div>
        )}
      </Card>

      <Card className="p-6 border-border/60">
        <h2 className="font-heading text-xl">Want to list your business?</h2>
        <p className="text-sm text-muted-foreground mt-1">Become a vendor to start receiving inquiries from consumers.</p>
        <Button asChild className="mt-4 rounded-full" data-testid="profile-become-vendor-btn">
          <Link to="/become-vendor">Become a Vendor</Link>
        </Button>
      </Card>
    </div>
  );
}
